import { TimerFactory } from '../../../common/_helpers/timer';
import {
	ComponentWithScrollingInterface,
	ScrollingHelperOptions,
} from '../_interfaces/component-with-scrolling.interface';

const DefaultHorizontalLabelOffset = 12;
const DefaultMinItemsForArrowsToBeShown = 3;

export default class ScrollingHelper {
	constructor(
		private context: ComponentWithScrollingInterface,
		private options: ScrollingHelperOptions = {
			horizontalLabelOffset: DefaultHorizontalLabelOffset,
			minItemsForArrowsToBeShown: DefaultMinItemsForArrowsToBeShown,
		}
	) {}

	/**
	 * Get the index of the item that is visible on the left hand side.
	 *
	 * @param currentScrollPosition  The number of pixels from the left that the element is scrolled by
	 * @param items  Array of elements to look through to find the left visible item
	 */
	getLeftVisibleItemIndex(
		currentScrollPosition: number,
		items: HTMLElement[]
	): number {
		return items.findIndex((btn) => {
			return btn.offsetLeft >= currentScrollPosition;
		});
	}

	getScrollPositionFor(labelButton: HTMLElement): number {
		return labelButton ? labelButton.offsetLeft : 0;
	}

	async windowResizeHandler(): Promise<null> {
		// Reset item label position and arrow button visibility, but only after this.scrollingContainer can be fetched
		if (
			this.context.scrollingContainer &&
			this.context.scrollableItems.length >=
				this.options.minItemsForArrowsToBeShown
		) {
			this.animateItems(0);
			this.context.showArrowRight = this.shouldRightArrowBeShown();
		}

		return;
	}

	animateSelectedItemIntoView(): void {
		const items = this.context.scrollableItems;
		const newScrollPos =
			this.getScrollPositionFor(items[this.context.selectedIndex]) -
			this.options.horizontalLabelOffset;
		this.scrollLabelContainerTo(newScrollPos);
	}

	/**
	 * Animate items so that the desired label is anchored to the left hand side, in a way that is relative to what is already visible
	 *
	 * @param relativePosition  A positive or negative number relative to what is the current left most visible label
	 *      Example: -1   Moves one position to the left, for example if the first item label is not visible, but the second is, animate to the first
	 *      Example: 0    Moves to the same position, but anchors any partly visible item to the left
	 *      Example: 1    Moves one position to the right
	 *      Example: 2    Moves two positions to the right
	 *      Etc.
	 */
	animateItems(relativePosition = 0): number {
		const currentScrollPos = this.context.scrollingContainer.scrollLeft;
		const leftVisibleIndex = this.getLeftVisibleItemIndex(
			currentScrollPos,
			this.context.scrollableItems
		);

		const newItem =
			this.context.scrollableItems[leftVisibleIndex + relativePosition];
		const newScrollPos =
			this.getScrollPositionFor(newItem) - this.options.horizontalLabelOffset;
		this.scrollLabelContainerTo(newScrollPos);

		return newScrollPos;
	}

	/**
	 * Safari will leave the focus state applied after a click event occurs. This function causes the focus to move away
	 * from the label after clicking so that it doesn't look like it's been tabbed onto with the keyboard.
	 * @param element
	 */
	blurFocusForSafari(element: HTMLElement, selector = 'button'): void {
		const innerButton: HTMLElement = element.querySelector(selector);
		if (innerButton) {
			innerButton.focus();
		}
	}

	focusOnLabel(
		index: number,
		selector = 'ds-button .ds-button-container'
	): void {
		const innerButtons =
			this.context.scrollingContainer.querySelectorAll<HTMLButtonElement>(
				selector
			);
		innerButtons[index].focus();
		this.context.focusedIndex = index;
	}

	/**
	 * When clicking on the left arrow
	 *  1) Always show the right arrow from now on. Logically if you can scroll to the left, there will be space to scroll back.
	 *  2) Work out the left most visible item index (leftVisibleIndex) eg. 2 if it's the third label/item
	 *  3) Get the scroll position in px for the item label that is just out of view on the left hand side (newScrollPos)
	 *  4) Scroll to that item label, animating the items to the left
	 *  5) If we are now all the way on the left, hide the left arrow because there's no further to go
	 */
	handleLeftArrowClick = (): void => {
		this.context.showArrowRight = true;
		this.animateItems(-1);

		this.context.showArrowLeft = this.shouldLeftArrowBeShown();
	};

	/**
	 * When clicking on the right arrow
	 *  1) Always show the left arrow from now on. Logically if you can scroll to the right, there will be space to scroll back.
	 *  2) Work out the left most visible item index (leftVisibleIndex) eg. 2 if it's the third label/item
	 *  3) Get the scroll position in px for the item label that is to the right of the left most label/item (newScrollPos)
	 *  4) Scroll to that item label, animating the items to the left
	 *  5) If we are now all the way on the right, hide the right arrow because there's no further to go
	 */
	handleRightArrowClick(): void {
		this.context.showArrowLeft = true;
		this.animateItems(1);

		// Wait for scrolling to complete, then potentially hide the right arrow
		TimerFactory(250)
			.tickTock()
			.then(() => {
				if (!this.shouldRightArrowBeShown()) {
					this.context.showArrowRight = false;
				}
			});
	}

	/**
	 * If a user action results in the item labels scroll position changing, show or hide the left/right arrows as needed.
	 * The scrollbar is hidden so this is not a normal scroll event as a result of someone using a scrollbar.
	 *
	 * Examples of how scroll events can still occur even though there's no scrollbar:
	 *    - Using the mousewheel
	 *    - Using the keyboard left/right arrows, making the focus shift
	 *    - Using your finger to frag left/right on a mobile device
	 */
	handleScroll = (): void => {
		this.context.showArrowRight = this.shouldRightArrowBeShown();
		this.context.showArrowLeft = this.shouldLeftArrowBeShown();
	};

	/**
	 * Determine if the left arrow should be shown by checking if the number of items is high enough to warrant arrows and
	 * by checking if the scrollLeft property is more than zero.
	 */
	shouldLeftArrowBeShown(): boolean {
		if (
			this.context.scrollableItems.length <
			this.options.minItemsForArrowsToBeShown
		) {
			return false;
		}
		return this.context.scrollingContainer.scrollLeft > 0;
	}

	/**
	 * Determine if the right arrow should be shown by checking if the number of items is high enough to warrant arrows and
	 * if the width of the visible item labels container is smaller than the labels inside.
	 * If it is smaller, then it indicates that scrolling can take place, which may warrant showing the right arrow.
	 * This function also takes into account the scrollLeft property which indicates if scrolling has occurred.
	 * And if the scrolling container is scrolled all the way to the right, this function will return false, otherwise true.
	 * 	 * scrollLeft Math.round is necessary beacuse in some instances when the user scrolls all the way to the right the
	 * this.context.scrollingContainer.scrollLeft is 0.5 pixels less than the maximum reachable number.
	 * That causes the function to return true when the button should dissapear.The round up fixes that.
	 */
	shouldRightArrowBeShown(): boolean {
		if (
			this.context.scrollableItems.length <
			this.options.minItemsForArrowsToBeShown
		) {
			return false;
		}
		return (
			Math.round(this.context.scrollingContainer.scrollLeft) +
				this.context.scrollingContainer.clientWidth <
			this.context.scrollingContainer.scrollWidth
		);
	}

	scrollLabelContainerTo(scrollPos: number): void {
		if (this.context.scrollingContainer.scrollTo) {
			this.context.scrollingContainer.scrollTo({
				top: 0,
				left: scrollPos,
				behavior: 'smooth',
			});
		} else {
			this.context.scrollingContainer.scrollLeft = scrollPos;
		}
	}

	isSelected(buttonIndex: number): boolean {
		return this.context.selectedIndex === buttonIndex;
	}

	hasWindowScrolledPastElement(el: HTMLElement, offset = 0): boolean {
		return el.getBoundingClientRect().top - offset <= 0;
	}
}
